
import React from 'react';
import QRCode from 'qrcode';
import { useState, useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Button } from '@/components/ui/button';
import { Download, FileImage, FileText } from 'lucide-react';

interface WindowStickerTemplateV2Props {
  providerSlug?: string;
  qrCodeUrl?: string;
  providerLogoUrl?: string;
  customHeading?: string;
  instructionText?: string;
  backgroundColor?: string;
  fontFamily?: string;
  titleSize?: string;
}

const WindowStickerTemplateV2 = ({ 
  providerSlug = "your-provider", 
  qrCodeUrl = "https://wellsuites.com",
  providerLogoUrl = "",
  customHeading = "BOOK A SESSION ONLINE",
  instructionText = "Scan QR code to make a booking",
  backgroundColor = "#ffffff",
  fontFamily = "Inter",
  titleSize = "text-3xl"
}: WindowStickerTemplateV2Props) => {
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [isExporting, setIsExporting] = useState(false);
  const [logoDataUrl, setLogoDataUrl] = useState<string>('');
  const stickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const generateQRCode = async () => {
      try {
        const dataUrl = await QRCode.toDataURL(qrCodeUrl, {
          width: 200,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        });
        setQrCodeDataUrl(dataUrl);
      } catch (error) {
        console.error('Error generating QR code:', error);
      }
    };

    generateQRCode();
  }, [qrCodeUrl]);

  // Convert provider logo to data URL to avoid CORS issues during export
  useEffect(() => {
    const convertLogoToDataUrl = async () => {
      if (!providerLogoUrl) {
        setLogoDataUrl('');
        return;
      }

      try {
        console.log('Converting logo to data URL:', providerLogoUrl);
        const response = await fetch(providerLogoUrl);
        const blob = await response.blob();
        
        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = reader.result as string;
          console.log('Logo converted to data URL successfully');
          setLogoDataUrl(dataUrl);
        };
        reader.onerror = () => {
          console.error('Failed to convert logo to data URL');
          setLogoDataUrl('');
        };
        reader.readAsDataURL(blob);
      } catch (error) {
        console.error('Error converting logo to data URL:', error);
        setLogoDataUrl('');
      }
    };

    convertLogoToDataUrl();
  }, [providerLogoUrl]);

  const handlePrint = () => {
    window.print();
  };

  const exportAsImage = async () => {
    if (!stickerRef.current) return;
    
    setIsExporting(true);
    try {
      console.log('Starting image export...');
      const canvas = await html2canvas(stickerRef.current, {
        backgroundColor: backgroundColor,
        scale: 2, // Higher quality
        useCORS: true,
        allowTaint: true,
        logging: true,
        foreignObjectRendering: false, // Disable for better compatibility
        imageTimeout: 15000, // Increase timeout
        removeContainer: true
      });
      
      console.log('Canvas created, dimensions:', canvas.width, 'x', canvas.height);
      
      const link = document.createElement('a');
      link.download = `window-sticker-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      console.log('Image export completed');
    } catch (error) {
      console.error('Error exporting as image:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const exportAsPDF = async () => {
    if (!stickerRef.current) return;
    
    setIsExporting(true);
    try {
      console.log('Starting PDF export...');
      const canvas = await html2canvas(stickerRef.current, {
        backgroundColor: backgroundColor,
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: true,
        foreignObjectRendering: false, // Disable for better compatibility
        imageTimeout: 15000, // Increase timeout
        removeContainer: true
      });
      
      console.log('Canvas created for PDF, dimensions:', canvas.width, 'x', canvas.height);
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a5' // A5 size as recommended
      });
      
      const imgWidth = 148; // A5 width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save(`window-sticker-${Date.now()}.pdf`);
      console.log('PDF export completed');
    } catch (error) {
      console.error('Error exporting as PDF:', error);
    } finally {
      setIsExporting(false);
    }
  };

  // Generate Google Fonts link
  const googleFontsLink = fontFamily !== 'Inter' && fontFamily !== 'Arial' && fontFamily !== 'Georgia' 
    ? `https://fonts.googleapis.com/css2?family=${fontFamily.replace(/\s+/g, '+')}:wght@400;700&display=swap`
    : null;

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      {/* Add Google Fonts link dynamically */}
      {googleFontsLink && (
        <link rel="stylesheet" href={googleFontsLink} />
      )}

      {/* Print Controls - Hidden when printing */}
      <div className="mb-8 print:hidden">
        <div className="max-w-md mx-auto bg-white p-6 rounded-lg shadow-lg">
          <h2 className="text-xl font-bold mb-4 text-center">Window Sticker Template V2</h2>
          <p className="text-gray-600 mb-4 text-center">
            Preview your window sticker before printing or exporting.
          </p>
          
          {/* Export Buttons */}
          <div className="space-y-3 mb-4">
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={exportAsImage}
                disabled={isExporting}
                variant="outline"
                className="w-full"
              >
                <FileImage className="w-4 h-4 mr-2" />
                {isExporting ? 'Exporting...' : 'Export PNG'}
              </Button>
              <Button
                onClick={exportAsPDF}
                disabled={isExporting}
                variant="outline"
                className="w-full"
              >
                <FileText className="w-4 h-4 mr-2" />
                {isExporting ? 'Exporting...' : 'Export PDF'}
              </Button>
            </div>
          </div>

          <button
            onClick={handlePrint}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 transition-colors"
          >
            Print Sticker
          </button>
        </div>
      </div>

      {/* Window Sticker Template V2 */}
      <div className="max-w-md mx-auto">
        <div 
          ref={stickerRef}
          className="border-4 border-dashed border-gray-300 print:border-none print:shadow-none shadow-lg rounded-lg overflow-hidden" 
          style={{ backgroundColor }}
        >
          {/* Main Content Area */}
          <div className="p-8 text-center">
            {/* Provider Logo Section */}
            {logoDataUrl && (
              <div className="mb-8 flex justify-center">
                <div className="bg-white p-4 rounded-lg shadow-md border border-gray-200 max-w-48 w-full">
                  <img 
                    src={logoDataUrl} 
                    alt="Provider Logo" 
                    className="w-full h-auto max-h-20 object-contain mx-auto"
                    crossOrigin="anonymous"
                  />
                </div>
              </div>
            )}

            {/* Main Heading */}
            <div className="mb-8">
              <h1 
                className={`${titleSize} font-bold text-gray-900 leading-tight`}
                style={{ fontFamily: fontFamily }}
              >
                {customHeading}
              </h1>
            </div>

            {/* QR Code Section */}
            <div className="mb-6 flex justify-center">
              <div className="bg-white p-4 rounded-lg shadow-md border border-gray-200">
                {qrCodeDataUrl ? (
                  <img 
                    src={qrCodeDataUrl} 
                    alt="QR Code to book online" 
                    className="w-48 h-48 mx-auto"
                    crossOrigin="anonymous"
                  />
                ) : (
                  <div className="w-48 h-48 bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-500">Loading QR Code...</span>
                  </div>
                )}
              </div>
            </div>

            {/* Instructions */}
            <div className="mb-8">
              <p 
                className="text-lg text-gray-700"
                style={{ fontFamily: fontFamily }}
              >
                {instructionText}
              </p>
            </div>

            {/* Branding Section */}
            <div className="mt-8">
              <p className="text-xs text-gray-600 mb-2">powered by</p>
              <div className="flex items-center justify-center">
                <img 
                  src="/lovable-uploads/cfc5519d-985b-4b9d-a3a6-ecaa509a0dc4.png" 
                  alt="Wellsuites Logo" 
                  className="h-4 w-auto"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Print Guidelines - Hidden when printing */}
        <div className="mt-6 bg-white p-4 rounded-lg shadow print:hidden">
          <h3 className="font-semibold mb-2">Export & Print Guidelines:</h3>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• Export as PNG for digital use or sharing</li>
            <li>• Export as PDF for professional printing</li>
            <li>• Use high-quality sticker paper or cardstock</li>
            <li>• Print at 100% scale (no scaling)</li>
            <li>• Recommended size: A5 (148 × 210 mm)</li>
            <li>• Use color printer for best results</li>
          </ul>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            margin: 0;
            padding: 0;
          }
          
          .print\\:hidden {
            display: none !important;
          }
          
          .print\\:border-none {
            border: none !important;
          }
          
          .print\\:shadow-none {
            box-shadow: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default WindowStickerTemplateV2;
